import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { fetchSession, signOut as signOutRequest, type ClassificationState, type SessionPerson } from "../api/session";
import { onSessionExpired } from "../api/apiFetch";

/**
 * Everything the shell and every later screen need to know about who is signed in, reduced from
 * `GET /api/session/me` into the four states the phase 4 brief requires the shell to render
 * distinctly: loading, signed-out, unclassified (`UR-V-01`) and signed-in. A fifth state, `error`,
 * covers the session endpoint itself being unreachable (`QR-07`) — the portal must stay usable,
 * not blank, when that happens.
 */
type SessionSnapshot =
  | { status: "loading" }
  | { status: "signed-out" }
  | { status: "unclassified"; person: SessionPerson; classificationState: Exclude<ClassificationState, "Active">; impersonated: boolean }
  | { status: "signed-in"; person: SessionPerson; permissions: string[]; impersonated: boolean }
  | { status: "error"; message: string };

/** The session snapshot plus the actions a component may take on it. */
export interface SessionContextValue {
  snapshot: SessionSnapshot;
  /** Re-queries `GET /api/session/me` and updates the snapshot. */
  refresh: () => Promise<void>;
  /** Signs the session out on the server, then refreshes the snapshot. */
  signOut: () => Promise<void>;
}

const SessionContext = createContext<SessionContextValue | undefined>(undefined);

/**
 * Loads the session once on mount and makes it available to the whole application through
 * {@link useSession}. Kept deliberately separate from routing and presentation — nothing here
 * knows what a "shell" or a "route" is, only what the backend-for-frontend says about the caller.
 *
 * @param props - The provider's children.
 */
export function SessionProvider({ children }: { children: ReactNode }): React.JSX.Element {
  const [snapshot, setSnapshot] = useState<SessionSnapshot>({ status: "loading" });

  const refresh = useCallback(async () => {
    const result = await fetchSession();

    switch (result.kind) {
      case "signed-out":
        setSnapshot({ status: "signed-out" });
        return;
      case "error":
        setSnapshot({ status: "error", message: result.message });
        return;
      case "authenticated": {
        const { person, classificationState, permissions, impersonated } = result.response;
        if (classificationState === "Active") {
          setSnapshot({ status: "signed-in", person, permissions, impersonated: impersonated === true });
        } else {
          setSnapshot({ status: "unclassified", person, classificationState, impersonated: impersonated === true });
        }
      }
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // A 401 from any API call means the session may have gone since it was checked (it expired, or an impersonation timed
  // out), so check again. A burst of 401s from one page's calls is one check, not one each.
  useEffect(() => {
    let checking = false;
    return onSessionExpired(() => {
      if (checking) {
        return;
      }

      checking = true;
      void refresh().finally(() => {
        checking = false;
      });
    });
  }, [refresh]);

  const signOut = useCallback(async () => {
    await signOutRequest();
    await refresh();
  }, [refresh]);

  const value = useMemo<SessionContextValue>(() => ({ snapshot, refresh, signOut }), [snapshot, refresh, signOut]);

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

/**
 * Reads the current session snapshot and its actions. Must be called from within a
 * {@link SessionProvider}.
 *
 * @throws {Error} when called outside a {@link SessionProvider}.
 */
export function useSession(): SessionContextValue {
  const context = useContext(SessionContext);
  if (context === undefined) {
    throw new Error("useSession must be used within a SessionProvider.");
  }

  return context;
}
